import React, { useMemo, useState, useCallback, useEffect } from "react"
import {
  Table,
  HStack,
  Text,
  VStack,
  Box,
  Button,
  Input,
  PopoverRoot,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverPositioner,
  Skeleton,
  Center,
  Separator,
  Spinner,
  Link as TriggerLink,
} from "@chakra-ui/react"
import Link from "@hydrologiq/shared/components/Link"
import {
  FaSortUp,
  FaSortDown,
  FaSort,
  FaFilter,
  FaXmark,
  FaAngleUp,
  FaAngleDown,
  FaTriangleExclamation,
} from "react-icons/fa6"
import StatusBadge, { StatusType } from "@hydrologiq/shared/components/StatusBadge"
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import * as gqlTypes from "@hydrologiq/gql/graphql"
import { SegmentedControl } from "@hydrologiq/shared/chakra/SegmentedControl"
import ErrorAlert from "@hydrologiq/shared/components/ErrorAlert"
import { NativeSelectRoot, NativeSelectField } from "@hydrologiq/shared/chakra/NativeSelect"
import ActivitiesExport from "./ActivitiesExport"
import { formatActivityDate } from "@hydrologiq/shared/utils/activities/dateFormatting"
import CurrentOrderDialog from "@hydrologiq/shared/components/CurrentOrderDialog"
import { MultiAssetDisplay } from "./MultiAssetDisplay"
import ActivityManage from "./ActivityManage"
import { formatActivityKind } from "@hydrologiq/shared/utils/activities/activityStatus"
import { DEFAULT_SORT } from "@hydrologiq/shared/hooks/activities/useActivities"
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "@hydrologiq/shared/copy/constants"
import { Field } from "@hydrologiq/shared/chakra/Field"
import { useRoute } from "@hydrologiq/shared/providers/helpers/useRoute"
import { useIsSectionEnabled } from "@hydrologiq/shared/providers/SectionFeatures"

// Helper function to generate page numbers for pagination
function getPageNumbers(totalPages: number, currentPage: number): (number | string)[] {
  const delta = 2 // Number of pages to show around current page
  const pages: (number | string)[] = []

  if (totalPages <= 7) {
    // Show all pages if total is 7 or less
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i)
    }
  } else {
    // Always show first page
    pages.push(1)

    if (currentPage > delta + 2) {
      pages.push("...")
    }

    // Show pages around current page
    const start = Math.max(2, currentPage - delta + 1)
    const end = Math.min(totalPages - 1, currentPage + delta + 1)

    for (let i = start; i <= end; i++) {
      pages.push(i)
    }

    if (currentPage < totalPages - delta - 1) {
      pages.push("...")
    }

    // Always show last page if there are multiple pages
    if (totalPages > 1) {
      pages.push(totalPages)
    }
  }

  return pages
}

// Extend TanStack Table's column meta to include filter variants
declare module "@tanstack/react-table" {
  interface ColumnMeta<TData, TValue> {
    filterVariant?: "text" | "select" | "range"
    filterFormatter?: (value: any) => string
  }
}

export interface ServerFiltering {
  filters: {
    status?: string
    kind?: string
    customer?: string
    site?: string
    asset?: string
    name?: string
    last_modified?: string
    source_site?: string
    destination_site?: string
  }
  sort: {
    field?: string
    direction?: "asc" | "desc"
  } | null
  defaultFilters?: ServerFiltering["filters"]
  defaultSort?: ServerFiltering["sort"]
  onFiltersChange: (filters: ServerFiltering["filters"]) => void
  onSortChange: (sort: ServerFiltering["sort"]) => void
  onClearAll: () => void
}

interface ActivitiesTableAdvancedProps {
  prefix?: string
  // Order with activities (for context calculations)
  order?: gqlTypes.Order
  asset?: gqlTypes.Asset
  // Activities data
  activities?: gqlTypes.Activity[]
  loading?: boolean
  refreshing?: boolean
  error?: string | null
  onRetry?: () => void
  // Server pagination props
  serverPagination?: {
    pagination: gqlTypes.PaginationMeta
    onPageChange: (page: number) => void
    onPageSizeChange: (pageSize: number) => void
  }
  // Server filtering props
  serverFiltering?: ServerFiltering
  // Filter options for smart dropdowns
  filterOptions?: gqlTypes.ActivityFilterOptions
  // Optional header actions
  headerActions?: React.ReactNode
  actionsRight?: React.ReactNode
  // Callback when activity is changed (for cache updates)
  onActivityChanged?: () => void | Promise<void>
  hideColumns?: string[]
  // Default sort direction (true = asc, false = desc)
  defaultSortAsc?: boolean
  useGroups?: boolean
  // Allow deletion of activities from the edit dialog (default: false / readonly)
  allowDelete?: boolean
}

// Debounced input component for text filters
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 300,
  ...props
}: {
  value: string | number
  onChange: (value: string | number) => void
  debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">) {
  const [value, setValue] = useState(initialValue)

  React.useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value])

  return <Input {...props} value={value} onChange={(e) => setValue(e.target.value)} size="sm" />
}

// Filter component for individual columns
function Filter({ column, filterOptions }: { column: any; filterOptions?: gqlTypes.ActivityFilterOptions }) {
  const { filterVariant } = column.columnDef.meta ?? {}
  const columnFilterValue = column.getFilterValue()

  const sortedUniqueValues = useMemo(() => {
    if (filterVariant === "range") {
      return []
    }

    const columnId = column.id

    // Check if we have server-side filter options for this column
    if (filterOptions) {
      if (columnId === "order_name" && filterOptions.orders) {
        return filterOptions.orders.map((option) => ({
          label: option.name, // Display name for dropdown
          value: option.value, // Use ID for filtering
        }))
      } else if (columnId === "customer" && filterOptions.customers) {
        return filterOptions.customers.map((option) => ({
          label: option.name,
          value: option.value, // Use ID for filtering
        }))
      } else if (columnId === "site" && filterOptions.sites) {
        return filterOptions.sites.map((option) => ({
          label: option.name,
          value: option.value, // Use ID for filtering
        }))
      } else if (columnId === "source_site" && filterOptions.source_sites) {
        return filterOptions.source_sites.map((option) => ({
          label: option.name,
          value: option.value, // Use ID for filtering
        }))
      } else if (columnId === "destination_site" && filterOptions.destination_sites) {
        return filterOptions.destination_sites.map((option) => ({
          label: option.name,
          value: option.value, // Use ID for filtering
        }))
      } else if (columnId === "asset" && filterOptions.assets) {
        return filterOptions.assets.map((option) => ({
          label: option.name,
          value: option.value, // Use ID for filtering
        }))
      } else if (columnId === "batch" && filterOptions.batches) {
        return filterOptions.batches.map((option) => ({
          label: option.name,
          value: option.value, // Use ID for filtering
        }))
      } else if (columnId === "status" && filterOptions.statuses) {
        return filterOptions.statuses.map((option) => ({
          label: option.name,
          value: option.value,
        }))
      } else if (columnId === "activity_type" && filterOptions.kinds) {
        return filterOptions.kinds.map((option) => ({
          label: option.name,
          value: option.value,
        }))
      }
    }

    // Fallback to client-side unique values
    const allValues = Array.from(column.getFacetedUniqueValues().keys())

    const uniqueValues = allValues
      .filter((value) => value !== null && value !== undefined && value !== "")
      .sort()
      .slice(0, 5000)

    return uniqueValues.map((value) => ({
      label: value,
      value: value,
    }))
  }, [column.getFacetedUniqueValues(), filterVariant, filterOptions, column.id])

  if (filterVariant === "select") {
    // Get custom formatter from column meta if available
    const filterFormatter = column.columnDef.meta?.filterFormatter

    return (
      <NativeSelectRoot active={!!columnFilterValue}>
        <NativeSelectField
          value={columnFilterValue?.toString() ?? ""}
          onChange={(e) => column.setFilterValue(e.target.value || undefined)}
          items={[
            { label: "All", value: "" },
            ...sortedUniqueValues.map((item: any) => ({
              label: filterFormatter ? filterFormatter(item.value) : item.label,
              value: item.value,
            })),
          ]}
        />
      </NativeSelectRoot>
    )
  }

  // Text filter (default)
  return (
    <DebouncedInput
      type="text"
      value={(columnFilterValue ?? "") as string}
      onChange={(value) => column.setFilterValue(value)}
      placeholder={`Search... (${column.getFacetedUniqueValues().size})`}
      list={column.id + "list"}
    />
  )
}

// Color constants for consistent theming
const COLORS = {
  ACTIVE: "active", // Purple for active states
  HOVER: "highlight", // Blue for hover/open states
  INACTIVE: "primary", // Gray for inactive states
}

// Column header with combined sort and filter capabilities in a single popover
function ColumnHeader({
  header,
  columnFilters,
  filterOptions,
}: {
  header: any
  columnFilters: ColumnFiltersState
  filterOptions?: gqlTypes.ActivityFilterOptions
}) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)

  // Check if this column has an active filter
  const hasActiveFilter = columnFilters.some((filter) => filter.id === header.column.id && filter.value)

  // Get sort state
  const sortState = header.column.getIsSorted()

  // Check if column has any sort or filter capabilities
  const hasCapabilities = header.column.getCanSort() || header.column.getCanFilter()

  return (
    <HStack justify="space-between" w="full">
      {/* Column title and visual indicators */}
      <HStack flex={1} gap={2}>
        <Text>{flexRender(header.column.columnDef.header, header.getContext())}</Text>

        {/* Visual indicators - clickable triggers for popover */}
        <HStack gap={1}>
          {/* Sort indicator */}
          {header.column.getCanSort() && (
            <Box
              color={isPopoverOpen && !sortState ? COLORS.HOVER : sortState ? COLORS.ACTIVE : COLORS.INACTIVE}
              cursor="pointer"
              onClick={() => setIsPopoverOpen(!isPopoverOpen)}
              _hover={{ color: sortState ? undefined : COLORS.HOVER }}
            >
              {sortState === "asc" ? (
                <FaSortUp size={12} />
              ) : sortState === "desc" ? (
                <FaSortDown size={12} />
              ) : (
                <FaSort size={12} />
              )}
            </Box>
          )}

          {/* Filter indicator */}
          {header.column.getCanFilter() && (
            <Box
              color={
                isPopoverOpen && !hasActiveFilter ? COLORS.HOVER : hasActiveFilter ? COLORS.ACTIVE : COLORS.INACTIVE
              }
              cursor="pointer"
              onClick={() => setIsPopoverOpen(!isPopoverOpen)}
              _hover={{ color: hasActiveFilter ? undefined : COLORS.HOVER }}
            >
              <FaFilter size={12} />
            </Box>
          )}
        </HStack>
      </HStack>

      {/* Combined sort/filter popover - triggered by icons */}
      {hasCapabilities && (
        <PopoverRoot open={isPopoverOpen} onOpenChange={({ open }) => setIsPopoverOpen(open)}>
          <PopoverTrigger asChild>
            <Box />
          </PopoverTrigger>
          <PopoverPositioner>
            <PopoverContent w="250px">
              <PopoverBody>
                <VStack gap={3} align="stretch">
                  <Text fontWeight="bold">{flexRender(header.column.columnDef.header, header.getContext())}</Text>

                  {/* Sort options */}
                  {header.column.getCanSort() && (
                    <HStack gap={2} align="stretch" justifyContent="space-between">
                      <Center w="100px" justifyContent="flex-start">
                        <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                          Sort by
                        </Text>
                      </Center>
                      <HStack gap={2} justifyContent="space-between" width="100%">
                        <Box>
                          {sortState && (
                            <Button
                              variant={"hydrologiq_icon" as any}
                              size="sm"
                              p={1}
                              onClick={() => {
                                header.column.clearSorting()
                                setIsPopoverOpen(false)
                              }}
                            >
                              <FaXmark size={12} />
                            </Button>
                          )}
                        </Box>
                        <SegmentedControl
                          value={sortState === "asc" ? "asc" : sortState === "desc" ? "desc" : ""}
                          onValueChange={(details) => {
                            if (details.value === "asc") {
                              header.column.toggleSorting(false)
                            } else if (details.value === "desc") {
                              header.column.toggleSorting(true)
                            } else {
                              header.column.clearSorting()
                            }
                            setIsPopoverOpen(false)
                          }}
                          size="sm"
                          variant={"hydrologiq" as any}
                          items={[
                            {
                              value: "asc",
                              label: "Asc",
                              icon: FaAngleUp,
                            },
                            {
                              value: "desc",
                              label: "Desc",
                              icon: FaAngleDown,
                            },
                          ]}
                        />
                      </HStack>
                    </HStack>
                  )}

                  {/* Separator between sort and filter */}
                  {header.column.getCanSort() && header.column.getCanFilter() && <Separator />}

                  {/* Filter options */}
                  {header.column.getCanFilter() && (
                    <HStack gap={2} align="stretch" justifyContent="space-between">
                      <Center w="100px" justifyContent="flex-start">
                        <Text fontSize="xs" textAlign="left" fontWeight="medium" color="fg.muted">
                          Filter by
                        </Text>
                      </Center>
                      <HStack width="100%" justifyContent="space-between">
                        {hasActiveFilter && (
                          <Button
                            variant={"hydrologiq_icon" as any}
                            size="sm"
                            p={1}
                            onClick={() => {
                              header.column.setFilterValue(undefined)
                              setIsPopoverOpen(false)
                            }}
                          >
                            <FaXmark size={12} />
                          </Button>
                        )}
                        <Filter column={header.column} filterOptions={filterOptions} />
                      </HStack>
                    </HStack>
                  )}
                </VStack>
              </PopoverBody>
            </PopoverContent>
          </PopoverPositioner>
        </PopoverRoot>
      )}
    </HStack>
  )
}

enum ActivityGroups {
  All = "All",
  Fills = "Filling",
  Movements = "Movement",
  Fulfilments = "Fulfilment",
  Holds = "Hold",
  Inspections = "Inspection",
  Maintenances = "Maintenance",
}

function ActivitiesTableAdvanced({
  prefix = "Activity",
  asset,
  activities,
  loading,
  refreshing = false,
  error,
  onRetry,
  serverPagination,
  serverFiltering,
  filterOptions,
  headerActions,
  actionsRight,
  onActivityChanged,
  hideColumns = [],
  defaultSortAsc = true,
  useGroups = false,
  allowDelete = false,
}: ActivitiesTableAdvancedProps) {
  const isPlannerEnabled = useIsSectionEnabled("planner")
  const isCustomersEnabled = useIsSectionEnabled("customers")
  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.ORDER_ID]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ORDER_ID],
    [COPY_KEYS.HEADINGS.SITE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.SITE],
  })

  // Stable empty array reference to prevent unnecessary re-renders
  const emptyActivities = useMemo<gqlTypes.Activity[]>(() => [], [])
  const activitiesData = activities || emptyActivities

  // Table-level state for controlled edit dialog
  const [editingActivityId, setEditingActivityId] = useState<string | null>(null)
  const [editingAssetId, setEditingAssetId] = useState<string | undefined>(undefined)

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const sortingDefaults = useMemo(() => {
    return [
      {
        id: DEFAULT_SORT.field || "",
        desc: !defaultSortAsc,
      },
    ]
  }, [defaultSortAsc])
  const [sorting, setSorting] = useState<SortingState>(sortingDefaults)
  const [clientPagination, setClientPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })

  // Order dialog state
  const [selectedOrder, setSelectedOrder] = useState<gqlTypes.Order | null>(null)
  const [isOrderDialogOpen, setIsOrderDialogOpen] = useState(false)

  const isServerPaginated = !!serverPagination
  const isServerFiltered = !!serverFiltering

  const paginationState = isServerPaginated
    ? {
      pageIndex: (serverPagination.pagination.page || 1) - 1,
      pageSize: serverPagination.pagination.per_page || 20,
    }
    : clientPagination

  // Convert server filters to TanStack Table format for display
  const serverColumnFilters = useMemo(() => {
    if (!isServerFiltered) return []

    const filters: ColumnFiltersState = []
    const { filters: serverFilters } = serverFiltering

    if (serverFilters.name) filters.push({ id: "order_name", value: serverFilters.name })
    if (serverFilters.status) filters.push({ id: "status", value: serverFilters.status })
    if (serverFilters.kind) filters.push({ id: "kind", value: serverFilters.kind })
    if (serverFilters.customer) filters.push({ id: "customer", value: serverFilters.customer })
    if (serverFilters.site) filters.push({ id: "site", value: serverFilters.site })
    if (serverFilters.asset) filters.push({ id: "asset", value: serverFilters.asset })
    if (serverFilters.source_site) filters.push({ id: "source_site", value: serverFilters.source_site })
    if (serverFilters.destination_site) filters.push({ id: "destination_site", value: serverFilters.destination_site })
    return filters
  }, [isServerFiltered, serverFiltering])

  // Convert server sort to TanStack Table format
  const serverSorting = useMemo(() => {
    if (!isServerFiltered || !serverFiltering.sort) return []

    return [
      {
        id: serverFiltering.sort.field || "",
        desc: serverFiltering.sort.direction === "desc",
      },
    ]
  }, [isServerFiltered, serverFiltering])

  // Custom filter/sort handlers for server-side filtering
  const handleColumnFiltersChange = useCallback(
    (updater: any) => {
      if (isServerFiltered) {
        // Convert TanStack Table filter to server filter
        const newFilters = typeof updater === "function" ? updater(serverColumnFilters) : updater
        const serverFilters: any = {}

        newFilters.forEach((filter: any) => {
          if (filter.value) {
            // Map column IDs to server filter field names
            switch (filter.id) {
              case "order_name":
                serverFilters.name = filter.value
                break
              case "status":
                serverFilters.status = filter.value
                break
              case "activity_type":
                serverFilters.kind = filter.value
                break
              case "customer":
                serverFilters.customer = filter.value
                break
              case "site":
                serverFilters.site = filter.value
                break
              case "source_site":
                serverFilters.source_site = filter.value
                break
              case "destination_site":
                serverFilters.destination_site = filter.value
                break
              case "asset":
                serverFilters.asset = filter.value
                break
              case "batch":
                serverFilters.batch = filter.value
                break
              case "last_modified":
                serverFilters.last_modified = filter.value
                break
              default:
                // Fallback to using the column ID as the filter key
                serverFilters[filter.id] = filter.value
            }
          }
        })
        console.log(serverFilters)
        serverFiltering?.onFiltersChange(serverFilters)
      } else {
        setColumnFilters(updater)
      }
    },
    [isServerFiltered, serverColumnFilters, serverFiltering]
  )

  const handleSortingChange = useCallback(
    (updater: any) => {
      if (isServerFiltered) {
        if (typeof updater === "function") {
          const currentSorting = serverSorting
          const newSorting = updater(currentSorting)

          if (newSorting.length > 0) {
            const sort = newSorting[0]
            serverFiltering?.onSortChange({
              field: sort.id,
              direction: sort.desc ? "desc" : "asc",
            })
          } else {
            serverFiltering?.onSortChange(null)
          }
        } else {
          if (updater.length > 0) {
            const sort = updater[0]
            serverFiltering?.onSortChange({
              field: sort.id,
              direction: sort.desc ? "desc" : "asc",
            })
          } else {
            serverFiltering?.onSortChange(null)
          }
        }
      } else {
        setSorting(updater || sortingDefaults)
      }
    },
    [isServerFiltered, serverSorting, serverFiltering]
  )

  // Determine if we're in single-order mode (no server filtering means single order view)
  const isSingleOrderMode = !serverFiltering

  useEffect(() => {
    if (isSingleOrderMode) setGrouping(ActivityGroups.Fulfilments)
  }, [isSingleOrderMode])

  // Check if any activities have orders to determine if order columns should be shown
  const hasAnyOrders = useMemo(() => {
    if (!activities || activities.length === 0) return false
    return activities.some((activity) => activity.order !== null && activity.order !== undefined)
  }, [activities])

  const [grouping, setGrouping] = useState<ActivityGroups>(ActivityGroups.All)

  // EditActivityCell - extracted outside useMemo to prevent recreation
  const EditActivityCell = useCallback(
    ({ row, accessorKey }: { row: any; accessorKey: string }) => {
      const activity = row.original
      const value = formatActivityKind(activity[accessorKey])

      if (
        (accessorKey === "kind" && activity.name && !hideColumns.includes("name")) ||
        (accessorKey === "name" && activity.kind && !hideColumns.includes("kind") && !!hideColumns.includes("name"))
      ) {
        return <Text>{value ?? "-"}</Text>
      }

      return (
        <TriggerLink
          variant="primary"
          cursor="pointer"
          onClick={() => {
            setEditingActivityId(activity.id)
            setEditingAssetId(activity.assets?.[0]?.id)
          }}
        >
          {value}
        </TriggerLink>
      )
    },
    [hideColumns]
  )

  useEffect(() => {
    // Change server side filtering for the activity kind based on the grouping
    const changeServerFilter = (kind?: gqlTypes.ActivityKind) => {
      serverFiltering?.onFiltersChange({
        kind: kind,
      })
    }
    switch (grouping) {
      case ActivityGroups.Fills:
        changeServerFilter(gqlTypes.ActivityKind.Filling)
        break
      case ActivityGroups.Movements:
        changeServerFilter(gqlTypes.ActivityKind.Movement)
        break
      case ActivityGroups.Fulfilments:
        changeServerFilter(gqlTypes.ActivityKind.Fulfilment)
        break
      case ActivityGroups.Holds:
        changeServerFilter(gqlTypes.ActivityKind.Hold)
        break
      case ActivityGroups.Inspections:
        changeServerFilter(gqlTypes.ActivityKind.Inspection)
        break
      case ActivityGroups.Maintenances:
        changeServerFilter(gqlTypes.ActivityKind.Maintenance)
        break
      default:
        changeServerFilter(undefined)
        break
    }
  }, [grouping])

  // Define table columns
  const columns = useMemo<ColumnDef<gqlTypes.Activity, any>[]>(() => {
    const baseColumns: ColumnDef<gqlTypes.Activity, any>[] = []

    baseColumns.push({
      accessorKey: "name",
      id: "name",
      header: `${prefix} ID`,
      cell: ({ row }) => {
        return <EditActivityCell row={row} accessorKey="name" />
      },
      enableColumnFilter: false,
    })

    if (grouping === ActivityGroups.All) {
      baseColumns.push({
        accessorKey: "kind",
        id: "kind",
        header: `${prefix} Type`,
        cell: ({ row }) => {
          return <EditActivityCell row={row} accessorKey="kind" />
        },
        meta: {
          filterVariant: "select",
          filterFormatter: (value: any) => formatActivityKind(value as gqlTypes.ActivityKind),
        },
      })
    }

    // Only add order columns if we have activities with orders and not in single-order mode
    if (hasAnyOrders && !isSingleOrderMode) {
      baseColumns.push(
        {
          accessorKey: "order.name",
          id: "order_name",
          header: copy[COPY_KEYS.HEADINGS.ORDER_ID],
          cell: ({ row }) => {
            const activity = row.original
            const order = activity.order

            if (!order) return <Text>-</Text>

            return (
              <TriggerLink
                variant={"primary" as any}
                cursor="pointer"
                onClick={() => {
                  setSelectedOrder(order as gqlTypes.Order)
                  setIsOrderDialogOpen(true)
                }}
              >
                {order.name}
              </TriggerLink>
            )
          },
          meta: {
            filterVariant: "select",
          },
        }
        // {
        //   accessorKey: "order.quantity",
        //   id: "order_quantity",
        //   header: "Ordered (kg)",
        //   cell: ({ getValue }) => <Text>{getValue() || "n/a"}</Text>,
        //   enableColumnFilter: false,
        // }
      )
    }

    let cols = [
      ...baseColumns,
      {
        id: "asset",
        accessorFn: (row: gqlTypes.Activity) => row.assets?.[0]?.id || null,
        header: "Asset",
        cell: ({ row }: { row: any }) => {
          const assets = row.original.assets
          if (!assets || assets.length === 0) {
            return (
              <HStack gap={1}>
                <FaTriangleExclamation />
                <Text>No asset</Text>
              </HStack>
            )
          }
          return <MultiAssetDisplay assets={assets} />
        },
        meta: {
          filterVariant: "select",
        },
      },
      {
        id: "status",
        accessorFn: (row) => {
          // Convert GraphQL enum to display format
          const status = row.status || "scheduled"
          if (status === "completed") return "Completed"
          if (status === "started") return "In progress"
          return "Scheduled"
        },
        header: "Status",
        cell: ({ getValue }) => {
          const status = getValue() as string
          // Map activity status to badge status
          const mapStatusToBadge = (activityStatus: string): StatusType => {
            if (activityStatus === "Scheduled") return "scheduled"
            if (activityStatus === "In progress") return "in_progress"
            if (activityStatus === "Completed") return "completed"
            return "scheduled" // default
          }
          return <StatusBadge status={mapStatusToBadge(status)} />
        },
        meta: {
          filterVariant: "select",
        },
      },
      // Add delivery status column for fulfillments (only if prefix is "Fulfillment")
      ...(prefix === "Fulfillment"
        ? [
          {
            id: "delivery_status",
            accessorFn: (row: gqlTypes.Activity) => {
              // Get delivery activity status from source_links
              const deliveryLink = row.source_links?.[0]
              return deliveryLink?.activity_status || null
            },
            header: "Delivery Status",
            cell: ({ getValue }: { getValue: () => string | null }) => {
              const status = getValue()
              if (!status) return <Text fontSize="sm">-</Text>

              // Map activity status to badge status
              const mapStatusToBadge = (activityStatus: string): StatusType => {
                if (activityStatus === "scheduled") return "scheduled"
                if (activityStatus === "started") return "in_progress"
                if (activityStatus === "completed") return "completed"
                return "scheduled"
              }

              return <StatusBadge status={mapStatusToBadge(status)} />
            },
            enableColumnFilter: false,
            enableSorting: false,
          },
          {
            id: "collection_status",
            accessorFn: (row: gqlTypes.Activity) => {
              // Get collection activity status from target_links
              const collectionLink = row.target_links?.[0]
              return collectionLink?.activity_status || null
            },
            header: "Collection Status",
            cell: ({ getValue }: { getValue: () => string | null }) => {
              const status = getValue()
              if (!status) return <Text fontSize="sm">-</Text>

              // Map activity status to badge status
              const mapStatusToBadge = (activityStatus: string): StatusType => {
                if (activityStatus === "scheduled") return "scheduled"
                if (activityStatus === "started") return "in_progress"
                if (activityStatus === "completed") return "completed"
                return "scheduled"
              }

              return <StatusBadge status={mapStatusToBadge(status)} />
            },
            enableColumnFilter: false,
            enableSorting: false,
          },
        ]
        : []),

      {
        id: "start",
        accessorKey: "start",
        header: "Start",
        cell: ({ getValue }) => {
          const value = getValue()
          return <Text fontSize="sm">{value ? formatActivityDate(value as string) : "-"}</Text>
        },
        enableColumnFilter: false,
      },
      {
        accessorKey: "end",
        header: "End",
        cell: ({ getValue }) => {
          const value = getValue()
          return <Text fontSize="sm">{value ? formatActivityDate(value as string) : "-"}</Text>
        },
        enableColumnFilter: false,
      },
    ].filter((col) => !hideColumns.includes(col.id || ""))

    const getOptionalValue = ({ getValue }: { getValue: () => string | null }) => (
      <Text fontSize="sm">{getValue() ? (getValue() as string) : "-"}</Text>
    )

    const site = (
      objectKey: string = "site",
      accessorKey: string = "site.id",
      header: string = copy[COPY_KEYS.HEADINGS.SITE],
      enableFilter: boolean = true
    ) => {
      return {
        accessorKey: accessorKey,
        id: objectKey,
        header: header,
        enableColumnFilter: enableFilter,
        cell: ({ row }: { row: any }) => {
          const site = row.original[objectKey]
          const siteType = site?.type

          return site ? (
            site.customer_site_id || site.filling_site_id ? (
              <Link
                variant={"primary" as any}
                href={
                  siteType === "customer"
                    ? `/customers/${site.customer_site_id}/sites/${site.id}`
                    : `/filling/${site.filling_site_id}`
                }
                deLink={!isCustomersEnabled}
              >
                {site.name}
              </Link>
            ) : (
              <Text fontSize="sm">{site?.name}</Text>
            )
          ) : (
            "-"
          )
        },
        meta: {
          filterVariant: "select",
        },
      }
    }
    const quantityScheduled = {
      accessorKey: "quantity_scheduled",
      header: "Planned (kg)",
      cell: getOptionalValue,
      enableColumnFilter: false,
    }
    const quantityStarted = {
      accessorKey: "quantity_started",
      header: "Started (kg)",
      cell: getOptionalValue,
      enableColumnFilter: false,
    }
    const quantityCompleted = {
      accessorKey: "quantity_completed",
      header: "Completed (kg)",
      cell: getOptionalValue,
      enableColumnFilter: false,
    }
    const quantity = {
      accessorKey: "quantity",
      id: "quantity",
      header: "Quantity (kg)",
      cell: getOptionalValue,
      enableColumnFilter: false,
    }
    const tankLevel = {
      accessorKey: "fill_level",
      id: "fill_level",
      header: "Tank Level (kg)",
      cell: getOptionalValue,
      enableColumnFilter: false,
    }
    const batch = {
      accessorKey: "batch.name",
      id: "batch",
      header: "Batch",
      cell: getOptionalValue,
      meta: {
        filterVariant: "select",
      },
    }

    let additionalColumns: any[] = []
    switch (grouping) {
      case ActivityGroups.All:
        additionalColumns = [site(), quantityScheduled, tankLevel, batch]
        break
      case ActivityGroups.Fills:
        additionalColumns = [
          site(undefined, undefined, "Fill Site"),
          quantityScheduled,
          { ...quantityCompleted, header: "Filled (kg)" },
          tankLevel,
          batch,
        ]
        break
      case ActivityGroups.Movements:
        additionalColumns = [
          site("source_site", "source_site.id", "Source Site"),
          site("destination_site", "destination_site.id", "Destination Site"),
          quantityScheduled,
          { ...quantityCompleted, header: "Delivered (kg)" },
          tankLevel,
          batch,
        ]
        break
      case ActivityGroups.Fulfilments:
        additionalColumns = [
          site(undefined, undefined, "Customer site"),
          quantityScheduled,
          { ...quantityStarted, header: "Delivered (kg)" },
          { ...quantityCompleted, header: "Consumed (kg)" },
          tankLevel,
          batch,
        ]
        break
      case ActivityGroups.Holds:
        additionalColumns = [site(undefined, undefined, "Asset Site"), tankLevel]
        break
      case ActivityGroups.Inspections:
        additionalColumns = [site(undefined, undefined, "Asset Site"), tankLevel]
        break
      case ActivityGroups.Maintenances:
        additionalColumns = [site(undefined, undefined, "Asset Site"), tankLevel]
        break
    }
    cols = cols.concat(additionalColumns)
    return cols
  }, [isSingleOrderMode, hasAnyOrders, hideColumns, grouping])

  const table = useReactTable({
    data: activitiesData,
    columns,
    filterFns: {
      auto: (row, columnId, value) => {
        const cellValue = row.getValue(columnId)
        if (cellValue === null || cellValue === undefined) {
          return false
        }
        return String(cellValue).toLowerCase().includes(String(value).toLowerCase())
      },
    },
    state: {
      columnFilters: isServerFiltered ? serverColumnFilters : columnFilters,
      sorting: isServerFiltered ? serverSorting : sorting,
      pagination: paginationState,
    },
    onColumnFiltersChange: handleColumnFiltersChange,
    onSortingChange: handleSortingChange,
    onPaginationChange: isServerPaginated ? undefined : setClientPagination,
    manualPagination: isServerPaginated,
    manualFiltering: isServerFiltered,
    manualSorting: isServerFiltered,
    pageCount: isServerPaginated ? serverPagination?.pagination.total_pages : undefined,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    initialState: {
      pagination: {
        pageSize: 20,
      },
    },
  })


  // Show error state
  if (error && !loading) {
    return (
      <VStack gap={4}>
        <ErrorAlert title="Failed to Load Activities" message={error} onClose={onRetry ? undefined : () => { }} />
        {onRetry && (
          <Button variant="outline" onClick={() => onRetry()}>
            Try Again
          </Button>
        )}
      </VStack>
    )
  }

  // Show table with data or loading skeletons
  return (
    <VStack gap={4} align="stretch" ml={isSingleOrderMode ? "10px" : undefined}>
      {/* Header with actions and activity count */}
      <HStack justify="space-between">
        {/* Left side - Header actions */}
        <Box>
          {headerActions}{" "}
          {useGroups && (
            <Field
              label="Showing:"
              flexDirection="row"
              alignment="center"
              labelHeight="45px"
              width="fit-content"
              mt={"5px"}
            >
              <NativeSelectRoot width="auto">
                <NativeSelectField
                  value={grouping}
                  onChange={(e) => {
                    setGrouping(e.target.value as ActivityGroups)
                  }}
                  items={Object.entries(ActivityGroups).map(([key, value]) => ({
                    label: ActivityGroups[key as keyof typeof ActivityGroups],
                    value: value,
                  }))}
                ></NativeSelectField>
              </NativeSelectRoot>
            </Field>
          )}
        </Box>

        {/* Right side - Export, clear filters and activity count */}
        <HStack gap={3}>
          {!isSingleOrderMode && serverFiltering && (
            <ActivitiesExport filters={serverFiltering?.filters} sort={serverFiltering?.sort} grouping={grouping} />
          )}

          {!isSingleOrderMode && (
            <Button
              variant={"hydrologiq_secondary" as any}
              size="sm"
              onClick={() => {
                if (isServerFiltered) {
                  serverFiltering?.onClearAll()
                } else {
                  setColumnFilters([])
                  setSorting(sortingDefaults)
                }
                setGrouping(ActivityGroups.All)
              }}
              disabled={
                isServerFiltered
                  ? (() => {
                    const currentFilters = serverFiltering?.filters || {}
                    const defaultFilters = serverFiltering?.defaultFilters || {}
                    const currentSort = serverFiltering?.sort
                    const defaultSort = serverFiltering?.defaultSort

                    // Check if filters match defaults
                    const currentKeys = Object.keys(currentFilters) as Array<keyof typeof currentFilters>
                    const defaultKeys = Object.keys(defaultFilters) as Array<keyof typeof defaultFilters>
                    const filtersMatchDefaults =
                      currentKeys.length === defaultKeys.length &&
                      currentKeys.every((key) => currentFilters[key] === defaultFilters[key])

                    // Check if sort matches default
                    const sortMatchesDefault =
                      (!currentSort && !defaultSort) ||
                      (currentSort?.field === defaultSort?.field && currentSort?.direction === defaultSort?.direction)

                    return filtersMatchDefaults && sortMatchesDefault
                  })()
                  : columnFilters.length === 0 && sorting.length === 0
              }
            >
              Clear Filters
            </Button>
          )}

          <Box minW="160px" display="flex" justifyContent="flex-end" alignItems="center">
            {loading ? (
              <HStack gap={2}>
                <Spinner size="sm" color="blue.500" />
                <Text fontSize="sm" color="fg.muted" whiteSpace="nowrap">
                  Loading activities...
                </Text>
              </HStack>
            ) : refreshing ? (
              <HStack gap={2}>
                <Spinner size="sm" color="blue.400" />
                <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">
                  Refreshing data...
                </Text>
              </HStack>
            ) : (
              <Text fontSize="sm" color="fg.muted" whiteSpace="nowrap">
                {isServerPaginated
                  ? `${serverPagination.pagination.total_count} total activities`
                  : `${table.getPrePaginationRowModel().rows.length} activities found`}
              </Text>
            )}
          </Box>
          {actionsRight && (
            <Box borderLeft="1px solid" borderColor="border" pl={3}>
              {actionsRight}
            </Box>
          )}
        </HStack>
      </HStack>

      {/* Table */}
      <Box overflowX="auto" overflowY="visible">
        <Table.Root variant="hydrologiq" size="md">
          <Table.Header>
            {table.getHeaderGroups().map((headerGroup) => (
              <Table.Row key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Table.ColumnHeader key={header.id} style={{ width: header.getSize() }}>
                    {header.isPlaceholder ? null : (
                      <ColumnHeader
                        header={header}
                        columnFilters={isServerFiltered ? serverColumnFilters : columnFilters}
                        filterOptions={filterOptions}
                      />
                    )}
                  </Table.ColumnHeader>
                ))}
              </Table.Row>
            ))}
          </Table.Header>

          <Table.Body>
            {loading ? (
              // Loading skeleton rows
              [...Array(10)].map((_, index) => (
                <Table.Row height="60px" key={`skeleton-${index}`}>
                  {columns.map((_, colIndex) => (
                    <Table.Cell key={colIndex}>
                      <Skeleton height="20px" width="100%" />
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))
            ) : table.getRowModel().rows.length === 0 ? (
              // Empty state
              <Table.Row>
                <Table.Cell colSpan={columns.length}>
                  <Center py={10}>
                    <VStack gap={2}>
                      <Text fontSize="lg" fontWeight="semibold">
                        No activities
                      </Text>
                      {isPlannerEnabled && 
                        <HStack gap={1}>
                          <Text fontSize="md">Use the</Text>
                          <Link href={`/planner`} variant={"primary" as any}>
                            Planner
                          </Link>
                          <Text fontSize="md">to create activities.</Text>
                        </HStack>
                      }
                    </VStack>
                  </Center>
                </Table.Cell>
              </Table.Row>
            ) : (
              // Actual data rows
              table.getRowModel().rows.map((row) => (
                <Table.Row key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <Table.Cell key={cell.id} style={{ width: cell.column.getSize() }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </Box>

      {/* Unified Pagination */}
      {isServerPaginated && (
        <HStack justify="space-between" align="center">
          <Text fontSize="sm" color="fg.muted">
            {`Page ${serverPagination.pagination.page} of ${serverPagination.pagination.total_pages}`}
          </Text>

          <HStack gap={1}>
            {/* Previous button */}
            <Button
              variant={"hydrologiq_secondary" as any}
              size="sm"
              onClick={() => {
                serverPagination.onPageChange(serverPagination.pagination.page - 1)
              }}
              disabled={!serverPagination.pagination.has_prev}
            >
              &lt;
            </Button>

            {/* Page numbers */}
            {getPageNumbers(serverPagination.pagination.total_pages, serverPagination.pagination.page).map(
              (pageNumber, index) => (
                <React.Fragment key={index}>
                  {pageNumber === "..." ? (
                    <Text px={2} fontSize="sm" color="fg.subtle">
                      ...
                    </Text>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => {
                        serverPagination.onPageChange(Number(pageNumber))
                      }}
                      variant={
                        (pageNumber === serverPagination.pagination.page
                          ? "hydrologiq_primary"
                          : "hydrologiq_secondary") as any
                      }
                    >
                      {pageNumber}
                    </Button>
                  )}
                </React.Fragment>
              )
            )}

            {/* Next button */}
            <Button
              variant={"hydrologiq_secondary" as any}
              size="sm"
              onClick={() => {
                serverPagination.onPageChange(serverPagination.pagination.page + 1)
              }}
              disabled={!serverPagination.pagination.has_next}
            >
              &gt;
            </Button>
          </HStack>

          {/* Page size selector */}
          <HStack gap={2}>
            <NativeSelectRoot size="sm" width="auto">
              <NativeSelectField
                value={serverPagination.pagination.per_page}
                onChange={(e) => {
                  serverPagination.onPageSizeChange(Number(e.target.value))
                }}
              >
                <option value={5}>5 rows</option>
                <option value={10}>10 rows</option>
                <option value={25}>25 rows</option>
                <option value={50}>50 rows</option>
              </NativeSelectField>
            </NativeSelectRoot>
          </HStack>
        </HStack>
      )}

      {/* Order dialog */}
      {selectedOrder && (
        <CurrentOrderDialog
          order={{
            id: selectedOrder.id,
            name: selectedOrder.name,
            status: selectedOrder.status,
            start: selectedOrder.start,
            end: selectedOrder.end,
            quantity: selectedOrder.quantity,
            quantity_consumed: selectedOrder.quantity_consumed,
            customer: selectedOrder.sites?.[0]?.customer || null,
            site: selectedOrder.sites?.[0]?.site || null,
          }}
          open={isOrderDialogOpen}
          onOpenChange={setIsOrderDialogOpen}
          showMoreLink={false}
        />
      )}

      {/* Activity edit dialog - controlled at table level */}
      {editingActivityId && (
        <ActivityManage
          mode="edit"
          readonly
          allowDelete={allowDelete}
          activityId={editingActivityId}
          assetId={editingAssetId}
          asset={asset}
          activities={activitiesData}
          isOpen={!!editingActivityId}
          onClose={() => {
            setEditingActivityId(null)
            setEditingAssetId(undefined)
            onActivityChanged?.()
          }}
        />
      )}
    </VStack>
  )
}

export default ActivitiesTableAdvanced
